import { gql } from "@apollo/client";

import { GOAL_FRAGMENT, INVITE_FRAGMENT } from "@graphql/fragments";

export const ADD_GOAL = gql`
  mutation addGoal(
    $beneficiaryId: String!
    $goalType: GoalType
    $goalName: String
    $accountType: AccountType!
    $targetYear: Int
    $targetAmountInCents: Float
    $recurringDepositInCents: Int
    $recurringDepositSchedule: SipFrequency
    $projectedRateOfReturn: Float
    $contributionsAfter18: Boolean
    $harvestStandardDeduction: Boolean
    $harvestLongTermCapitalGains: Boolean
    $harvestRecommendedFloorUsd: Int
    $trumpAccountBalanceInCents: Int
    $trumpContributionAmountInCents: Int
    $trumpContributionFrequency: TrumpContributionFrequency
    $expectedTaxBillWithoutSidecarInCents: Float
    $expectedTaxBillWithSidecarInCents: Float
    $spendableWithoutSidecarInCents: Float
    $spendableWithSidecarInCents: Float
    $incomeAt18: IncomeAt18
  ) {
    addGoal(
      input: {
        beneficiaryId: $beneficiaryId
        goalType: $goalType
        goalName: $goalName
        accountType: $accountType
        targetYear: $targetYear
        targetAmountInCents: $targetAmountInCents
        recurringDepositInCents: $recurringDepositInCents
        recurringDepositSchedule: $recurringDepositSchedule
        projectedRateOfReturn: $projectedRateOfReturn
        contributionsAfter18: $contributionsAfter18
        harvestStandardDeduction: $harvestStandardDeduction
        harvestLongTermCapitalGains: $harvestLongTermCapitalGains
        harvestRecommendedFloorUsd: $harvestRecommendedFloorUsd
        trumpAccountBalanceInCents: $trumpAccountBalanceInCents
        trumpContributionAmountInCents: $trumpContributionAmountInCents
        trumpContributionFrequency: $trumpContributionFrequency
        expectedTaxBillWithoutSidecarInCents: $expectedTaxBillWithoutSidecarInCents
        expectedTaxBillWithSidecarInCents: $expectedTaxBillWithSidecarInCents
        spendableWithoutSidecarInCents: $spendableWithoutSidecarInCents
        spendableWithSidecarInCents: $spendableWithSidecarInCents
        incomeAt18: $incomeAt18
      }
    ) {
      id
      name
      description
      type
      targetAmountInCents
      currentAmountInCents
      startDate
      endDate
      status
      contributionsAfter18
      projectedRateOfReturn
    }
  }
`;

/**
 * Creates the beneficiary and their first goal in one call. Used by the Trump
 * Account Sidecar flow during onboarding, where the goal is created before the
 * deposit routes that would otherwise have added the beneficiary — doing it
 * atomically avoids leaving an account with a beneficiary but no goal.
 */
export const ADD_GOAL_WITH_BENEFICIARY = gql`
  mutation addGoalWithBeneficiary(
    $beneficiary: AddBeneficiaryInput!
    $accountType: AccountType!
    $goalType: GoalType
    $goalName: String
    $targetYear: Int
    $targetAmountInCents: Float!
    $recurringDepositInCents: Int
    $recurringDepositSchedule: SipFrequency
    $projectedRateOfReturn: Float!
    $contributionsAfter18: Boolean
    $harvestStandardDeduction: Boolean
    $harvestLongTermCapitalGains: Boolean
    $harvestRecommendedFloorUsd: Int
    $trumpAccountBalanceInCents: Int
    $trumpContributionAmountInCents: Int
    $trumpContributionFrequency: TrumpContributionFrequency
    $expectedTaxBillWithoutSidecarInCents: Float
    $expectedTaxBillWithSidecarInCents: Float
    $spendableWithoutSidecarInCents: Float
    $spendableWithSidecarInCents: Float
    $incomeAt18: IncomeAt18
  ) {
    addGoalWithBeneficiary(
      input: {
        beneficiary: $beneficiary
        accountType: $accountType
        goalType: $goalType
        goalName: $goalName
        targetYear: $targetYear
        targetAmountInCents: $targetAmountInCents
        recurringDepositInCents: $recurringDepositInCents
        recurringDepositSchedule: $recurringDepositSchedule
        projectedRateOfReturn: $projectedRateOfReturn
        contributionsAfter18: $contributionsAfter18
        harvestStandardDeduction: $harvestStandardDeduction
        harvestLongTermCapitalGains: $harvestLongTermCapitalGains
        harvestRecommendedFloorUsd: $harvestRecommendedFloorUsd
        trumpAccountBalanceInCents: $trumpAccountBalanceInCents
        trumpContributionAmountInCents: $trumpContributionAmountInCents
        trumpContributionFrequency: $trumpContributionFrequency
        expectedTaxBillWithoutSidecarInCents: $expectedTaxBillWithoutSidecarInCents
        expectedTaxBillWithSidecarInCents: $expectedTaxBillWithSidecarInCents
        spendableWithoutSidecarInCents: $spendableWithoutSidecarInCents
        spendableWithSidecarInCents: $spendableWithSidecarInCents
        incomeAt18: $incomeAt18
      }
    ) {
      beneficiary {
        id
      }
      goal {
        id
        name
        description
        type
        targetAmountInCents
        currentAmountInCents
        startDate
        endDate
        status
        contributionsAfter18
        projectedRateOfReturn
      }
    }
  }
`;

export const EDIT_GOAL = gql`
  mutation editGoal(
    $goalId: String!
    $goalName: String
    $goalType: GoalType
    $projectedRateOfReturn: Float
    $contributionsAfter18: Boolean
    $targetYear: Int
    $targetAmountInCents: Float
    $trumpAccountBalanceInCents: Int
    $trumpContributionAmountInCents: Int
    $trumpContributionFrequency: TrumpContributionFrequency
    $expectedTaxBillWithoutSidecarInCents: Float
    $expectedTaxBillWithSidecarInCents: Float
    $spendableWithoutSidecarInCents: Float
    $spendableWithSidecarInCents: Float
    $incomeAt18: IncomeAt18
  ) {
    editGoal(
      input: {
        goalId: $goalId
        goalName: $goalName
        goalType: $goalType
        projectedRateOfReturn: $projectedRateOfReturn
        contributionsAfter18: $contributionsAfter18
        targetYear: $targetYear
        targetAmountInCents: $targetAmountInCents
        trumpAccountBalanceInCents: $trumpAccountBalanceInCents
        trumpContributionAmountInCents: $trumpContributionAmountInCents
        trumpContributionFrequency: $trumpContributionFrequency
        expectedTaxBillWithoutSidecarInCents: $expectedTaxBillWithoutSidecarInCents
        expectedTaxBillWithSidecarInCents: $expectedTaxBillWithSidecarInCents
        spendableWithoutSidecarInCents: $spendableWithoutSidecarInCents
        spendableWithSidecarInCents: $spendableWithSidecarInCents
        incomeAt18: $incomeAt18
      }
    ) {
      id
      name
      description
      type
      targetAmountInCents
      currentAmountInCents
      startDate
      endDate
      status
      contributionsAfter18
      projectedRateOfReturn
      trumpAccountBalanceInCents
      trumpContributionAmountInCents
      trumpContributionFrequency
    }
  }
`;

export const UPDATE_HARVEST_ACCOUNT_CONFIGS = gql`
  mutation updateHarvestAccountConfigs(
    $accountId: String!
    $harvestStandardDeduction: Boolean
    $harvestLongTermCapitalGains: Boolean
    $harvestRecommendedFloorUsd: Int
  ) {
    updateHarvestAccountConfigs(
      input: {
        accountId: $accountId
        harvestStandardDeduction: $harvestStandardDeduction
        harvestLongTermCapitalGains: $harvestLongTermCapitalGains
        harvestRecommendedFloorUsd: $harvestRecommendedFloorUsd
      }
    ) {
      id
      harvestStandardDeduction
      harvestLongTermCapitalGains
      harvestRecommendedFloorUsd
    }
  }
`;

export const INVITE_TO_SHARE_GOAL = gql`
  ${INVITE_FRAGMENT}
  mutation inviteToShareGoal($goalId: String!, $phoneNumber: String!) {
    inviteToShareGoal(input: { goalId: $goalId, phoneNumber: $phoneNumber }) {
      ...InviteFragment
    }
  }
`;

export const DELETE_INVITE_TO_SHARE_GOAL = gql`
  mutation deleteInviteToShareGoal($shareGoalInviteId: String!) {
    deleteInviteToShareGoal(input: { shareGoalInviteId: $shareGoalInviteId })
  }
`;

export const RESEND_INVITE_TO_SHARE_GOAL = gql`
  ${INVITE_FRAGMENT}
  mutation resendInviteToShareGoal($shareGoalInviteId: String!) {
    resendInviteToShareGoal(input: { shareGoalInviteId: $shareGoalInviteId }) {
      ...InviteFragment
    }
  }
`;

export const ACTION_SHARE_GOAL_INVITE = gql`
  ${GOAL_FRAGMENT}
  mutation actionShareGoalInvite(
    $shareGoalInviteId: String!
    $action: ShareGoalInviteAction!
    $accountType: AccountType
    $projectedRateOfReturn: Float
    $contributionsAfter18: Boolean
    $recurringDepositInCents: Int
    $recurringDepositSchedule: SipFrequency
    $targetAmountInCents: Float
  ) {
    actionShareGoalInvite(
      input: {
        shareGoalInviteId: $shareGoalInviteId
        action: $action
        accountType: $accountType
        projectedRateOfReturn: $projectedRateOfReturn
        contributionsAfter18: $contributionsAfter18
        recurringDepositInCents: $recurringDepositInCents
        recurringDepositSchedule: $recurringDepositSchedule
        targetAmountInCents: $targetAmountInCents
      }
    ) {
      ...GoalFragment
    }
  }
`;

export const REMOVE_PARTNER_FROM_GOAL = gql`
  ${GOAL_FRAGMENT}
  mutation removePartnerFromGoal($goalId: String!, $partnerId: String!) {
    removePartnerFromGoal(input: { goalId: $goalId, partnerId: $partnerId }) {
      ...GoalFragment
    }
  }
`;
